/**
 * A voice sample on its way to the server (ADR-0062): the recording's send — hashed once, the
 * same capture id on every try, a failure classified the way a recording's is — with a request of
 * its own, which names the kind `voice_sample` and no language or participants. Resolves to how
 * it went and never rejects.
 *
 * `sendVoiceSampleTake` needs no dialog and no hook: a flow that collected a take before its
 * subject existed (the enrolment) calls it once the subject does, and again with the same take to
 * retry.
 */
import type { VoiceSaveProblem } from '@/components/humint/voice/voice-types'
import type { CaptureUploadOptions } from '@/services/api/humint'
import type { RecordingUpload } from '@/services/api/humint-recordings'
import { humintVoiceApi, type VoiceSampleUpload } from '@/services/api/humint-voice'
import { captureSha256 } from '../capture/sha256'
import { holdRecording, type SendHooks, sendRecording } from '../recording/recording-upload'
import type { VoiceSampleTake } from './voice-sample-take'

/** Whose sample it is, and the encounter it joins. */
export interface VoiceSampleTarget {
  caseId: string
  subjectRef: string
  /** The encounter the sample joins (`enc-<uuid>`); null or absent for none. */
  encounterRef?: string | null
}

export type VoiceSendOutcome =
  /** The server stored it as this capture; its voiceprint is still to be computed. */
  | { kind: 'saved'; captureId: string }
  /** The caller's signal aborted it. */
  | { kind: 'cancelled' }
  /** Why not, and — `maySendAgain(problem)` — whether sending the same take again may help. */
  | { kind: 'failed'; problem: VoiceSaveProblem }

/** What sending needs from outside: the hash, the request and whether there is a connection. */
export interface VoiceSendDeps {
  hash: (blob: Blob) => Promise<string>
  upload: (
    target: VoiceSampleTarget,
    upload: VoiceSampleUpload,
    options: CaptureUploadOptions
  ) => Promise<unknown>
  online: () => boolean
}

/** The browser's: the capture hash, the voice-sample request, `navigator.onLine`. */
export const VOICE_SEND_DEPS: VoiceSendDeps = {
  hash: captureSha256,
  upload: (target, upload, options) =>
    humintVoiceApi.upload(target.caseId, target.subjectRef, upload, options),
  online: () => typeof navigator === 'undefined' || navigator.onLine,
}

/** A take's bytes never change, so its hash is computed once however often it is sent. */
const hashes = new WeakMap<Blob, Promise<string>>()

function hashOnce(blob: Blob, hash: VoiceSendDeps['hash']): Promise<string> {
  const known = hashes.get(blob)
  if (known) return known
  const computing = hash(blob)
  hashes.set(blob, computing)
  // A hash that failed is computed again by the next send.
  computing.catch(() => hashes.delete(blob))
  return computing
}

/** The request the capture endpoint takes for a voice sample, from the recording's. */
function voiceUploadOf(upload: RecordingUpload): VoiceSampleUpload {
  const { captureId, blob, sha256, extension, capturedAt, sameEncounterRef } = upload
  return { captureId, blob, sha256, extension, capturedAt, sameEncounterRef }
}

/**
 * Hash the take (the first time only) and send it as the subject's voice sample. `hooks` take the
 * upload's progress and a signal that aborts it.
 */
export async function sendVoiceSampleTake(
  take: VoiceSampleTake,
  target: VoiceSampleTarget,
  hooks: SendHooks = {},
  deps: VoiceSendDeps = VOICE_SEND_DEPS
): Promise<VoiceSendOutcome> {
  const { blob, startedAt, durationMs, interrupted } = take.audio
  const held = holdRecording(
    { blob, extension: take.extension, capturedAt: startedAt, durationMs, interrupted },
    { languages: [], encounterRef: target.encounterRef ?? null, participantRefs: [] },
    () => take.captureId
  )
  const outcome = await sendRecording(
    held,
    {
      hash: bytes => hashOnce(bytes, deps.hash),
      upload: (upload, options) =>
        deps.upload(
          target,
          { ...voiceUploadOf(upload), ...(take.fileName ? { imported: true } : {}) },
          options
        ),
      online: deps.online,
    },
    hooks
  )
  if (outcome.kind !== 'failed') return outcome
  // A voice sample names no language, so the server has none to refuse.
  const problem = outcome.problem === 'language' ? 'rejected' : outcome.problem
  return { kind: 'failed', problem }
}
